import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api';
import { Icon } from '../components/common/Icons';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { toast } from 'react-toastify';

export default function AccountPage() {
  const { user, logout, updateProfile: updateAuthUser } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    otp: '',
  });

  const [saving, setSaving] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        email: user.email || '',
        otp: '',
      });
    }
  }, [user]);

  // Countdown for resend OTP
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {} finally {
      logout();
      navigate('/login');
    }
  };

  const emailChanged = form.email.trim().toLowerCase() !== (user?.email || '').toLowerCase();

  const handleSendOtp = async () => {
    if (!form.email.trim()) {
      setError('Please provide a valid email address');
      return;
    }
    setError('');
    setSendingOtp(true);
    try {
      await authApi.sendEmailUpdateOtp({ email: form.email.trim() });
      setOtpSent(true);
      setCountdown(60);
      toast.info(`Verification code sent to ${form.email.trim()}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e?.preventDefault();
    setError('');

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (!form.email.trim()) {
      setError('Email address is required.');
      return;
    }
    if (emailChanged && !form.otp.trim()) {
      setError('Please enter the 6-digit verification code sent to your new email.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim() || undefined,
        email: form.email.trim().toLowerCase(),
        otp: emailChanged ? form.otp.trim() : undefined,
      };

      const { data: updatedProfile } = await authApi.updateProfile(payload);
      updateAuthUser(updatedProfile);
      setIsEditing(false);
      setOtpSent(false);
      setForm(prev => ({ ...prev, otp: '' }));
      toast.success('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError('');
    setOtpSent(false);
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        email: user.email || '',
        otp: '',
      });
    }
  };

  const fullName = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username || 'User';
  const initials = (user?.firstName?.[0] || user?.username?.[0] || 'U').toUpperCase();

  return (
    <div className="account-page-container">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <Icon name="user" size={24} />
            <span>Your Account</span>
          </h1>
        </div>

        <div className="page-header-actions">
          {!isEditing ? (
            <button
              type="button"
              className="btn-primary-custom"
              onClick={() => setIsEditing(true)}
            >
              <Icon name="edit" size={15} />
              <span>Edit Profile</span>
            </button>
          ) : (
            <button
              type="button"
              className="btn-ghost"
              onClick={handleCancelEdit}
              disabled={saving}
            >
              <span>Cancel</span>
            </button>
          )}
        </div>
      </div>

      <div className="account-cards-grid single-col">
        {/* Profile Card */}
        <div className="settings-card account-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">
              <Icon name="user" size={18} />
            </div>
            <div>
              <h2 className="settings-card-title">{isEditing ? 'Edit Profile Details' : 'Profile Details'}</h2>
            </div>
          </div>

          {!isEditing ? (
            <>
              <div className="settings-profile-badge">
                <div className="settings-profile-avatar">
                  {initials}
                </div>
                <div className="settings-profile-info">
                  <div className="settings-profile-name">{fullName}</div>
                  <div className="settings-profile-username">@{user?.username || 'user'}</div>
                </div>
              </div>

              <div className="settings-details-list">
                <div className="settings-detail-row">
                  <span className="detail-key">First Name</span>
                  <span className="detail-value">{user?.firstName || '-'}</span>
                </div>
                <div className="settings-detail-row">
                  <span className="detail-key">Last Name</span>
                  <span className="detail-value">{user?.lastName || '-'}</span>
                </div>
                <div className="settings-detail-row">
                  <span className="detail-key">Username</span>
                  <span className="detail-value">@{user?.username || '-'}</span>
                </div>
                <div className="settings-detail-row">
                  <span className="detail-key">Email</span>
                  <span className="detail-value" title={user?.email}>{user?.email || '-'}</span>
                </div>
                <div className="settings-detail-row">
                  <span className="detail-key">Phone</span>
                  <span className="detail-value">{user?.phoneNumber || 'Not provided'}</span>
                </div>
              </div>
            </>
          ) : (
            <form onSubmit={handleSaveProfile} className="account-edit-form" style={{ marginTop: '0.75rem' }}>
              {error && (
                <div className="search-error-alert" style={{ marginBottom: '1.25rem' }}>
                  <Icon name="alert-triangle" size={15} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-group-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="edit-first-name">
                    First Name <span className="req">*</span>
                  </label>
                  <input
                    id="edit-first-name"
                    className="form-input"
                    value={form.firstName}
                    onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                    placeholder="First Name"
                    required
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="edit-last-name">
                    Last Name <span className="req">*</span>
                  </label>
                  <input
                    id="edit-last-name"
                    className="form-input"
                    value={form.lastName}
                    onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label" htmlFor="edit-phone">
                  Phone Number
                </label>
                <input
                  id="edit-phone"
                  className="form-input"
                  value={form.phoneNumber}
                  onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-label" htmlFor="edit-email">
                  Email Address <span className="req">*</span>
                  {emailChanged && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--warning)', fontWeight: 500 }}>
                      (verification code required for new email)
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: '0.65rem' }}>
                  <input
                    id="edit-email"
                    type="email"
                    className="form-input"
                    value={form.email}
                    onChange={e => {
                      setForm(f => ({ ...f, email: e.target.value }));
                      setOtpSent(false);
                    }}
                    placeholder="you@domain.com"
                    required
                    style={{ flex: 1 }}
                  />
                  {emailChanged && (
                    <button
                      type="button"
                      className="btn-secondary-custom"
                      onClick={handleSendOtp}
                      disabled={sendingOtp || countdown > 0 || !form.email.trim()}
                      style={{ whiteSpace: 'nowrap', minWidth: '130px' }}
                    >
                      {sendingOtp ? 'Sending...' : countdown > 0 ? `Resend (${countdown}s)` : otpSent ? 'Resend Code' : 'Send Code'}
                    </button>
                  )}
                </div>
              </div>

              {emailChanged && (
                <div className="form-group" style={{ marginBottom: '1.5rem', background: 'var(--surface-2)', padding: '1rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <label className="form-label" htmlFor="edit-email-otp">
                    6-Digit Verification Code <span className="req">*</span>
                  </label>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem' }}>
                    Enter the code sent to {form.email} to verify ownership of this new email address.
                  </p>
                  <input
                    id="edit-email-otp"
                    type="text"
                    maxLength={6}
                    className="form-input"
                    value={form.otp}
                    onChange={e => setForm(f => ({ ...f, otp: e.target.value.replace(/\D/g, '') }))}
                    placeholder="••••••"
                    style={{ maxWidth: '200px', letterSpacing: '0.25em', textAlign: 'center', fontSize: '1.1rem', fontWeight: 600 }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)' }}>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary-custom"
                  disabled={saving || (emailChanged && (!form.otp || form.otp.length < 6))}
                >
                  {saving ? <div className="spinner sm" /> : <Icon name="check" size={15} />}
                  <span>{saving ? 'Saving...' : 'Save Profile'}</span>
                </button>
              </div>
            </form>
          )}

          {/* Log Out button at bottom of Profile Details card */}
          {!isEditing && (
            <div className="settings-profile-actions" style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '1.25rem' }}>
              <button
                type="button"
                className="btn-danger-outline"
                onClick={() => setShowLogoutConfirm(true)}
              >
                <Icon name="logout" size={15} />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirm Dialog */}
      {showLogoutConfirm && (
        <ConfirmDialog
          title="Sign Out"
          message="Are you sure you want to end your current session and sign out?"
          danger={false}
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </div>
  );
}
